import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ChatApiService } from '../chat-v1/matrix-chat/data/chat-api.service';
import { ChatFacade } from '../chat-v1/matrix-chat/state/chat.facade';
import { SubMenuService } from './submenu.service';
import { ToastService } from './toast.service';
import { UserProfileService } from './user-profile.service';
import { SocialService } from './social.service';
import { CardIdentity, CardParams, ChatArgs, resolveChatTarget } from '../shared/utils/vessel-card.logic';

/**
 * Acts on the boat card's chat button: routes through `resolveChatTarget` (a port of
 * Flutter's `_navigateToChat`) and opens the matching conversation.
 */
@Injectable({ providedIn: 'root' })
export class VesselCardChatService {
  constructor(
    private chatApi: ChatApiService,
    private chat: ChatFacade,
    private submenu: SubMenuService,
    private toast: ToastService,
    private router: Router,
    private profiles: UserProfileService,
    private social: SocialService,
  ) {}

  async open(args: ChatArgs, params: CardParams, me: CardIdentity): Promise<void> {
    const target = resolveChatTarget(args, params, me, params.vesselName ?? null);
    switch (target.kind) {
      case 'chat-list':
        this.submenu.setMessagesOpenState(true);
        await this.router.navigate(['/chat']);
        return;

      case 'direct': {
        try {
          // No usable id (the API sends "0" when it doesn't know the owner): boats on the live
          // map belong to dockmates, whose list carries real Keycloak ids — match by name.
          const candidate = target.targetUserId ?? (await this.dockmateIdByName(target.name));
          const keycloakId = candidate ? await this.toKeycloakId(candidate) : null;
          if (!keycloakId) {
            this.toast.info(`Can't start a chat with ${target.name} yet.`);
            return;
          }
          this.chat.keycloakIdToDisplayName.set(keycloakId, target.name);
          let room = await firstValueFrom(this.chatApi.getDmRoom(keycloakId));
          if (room?.blocked) return;
          let roomId = room?.matrix_room_id;
          if (!roomId) {
            room = await firstValueFrom(this.chatApi.createDmRoom(keycloakId));
            if (room?.blocked) return;
            roomId = room?.matrix_room_id;
          }
          if (!roomId) throw new Error('no room');
          await this.openInMessages(roomId);
        } catch {
          this.toast.error('Could not open the chat. Please try again.');
        }
        return;
      }

      case 'ride-along-group': {
        if (!target.tripId) {
          this.toast.info('This ride-along trip could not be identified.');
          return;
        }
        try {
          const room = await firstValueFrom(this.chatApi.openRideAlongRoom(target.tripId, target.name));
          if (!room?.matrix_room_id) throw new Error('no room');
          await this.openInMessages(room.matrix_room_id);
        } catch (err: any) {
          // 409 once the trip has ended: the server's own message says so.
          const detail = err?.error?.detail;
          this.toast.error(typeof detail === 'string' && detail ? detail : 'Could not open the group chat. Please try again.');
        }
        return;
      }

      case 'none':
        return;
    }
  }

  /** Exact (case-insensitive) display-name match against my dockmates; null when absent or ambiguous. */
  private async dockmateIdByName(name: string): Promise<string | null> {
    const wanted = name.trim().toLowerCase();
    if (!wanted) return null;
    try {
      const data: any = await firstValueFrom(this.social.myDockmates(1, 500, ''));
      const matches = (Array.isArray(data?.results) ? data.results : [])
        .map((item: any) => item?.user ?? {})
        .filter((u: any) => String(u.name ?? '').trim().toLowerCase() === wanted && u.user_id);
      return matches.length === 1 ? String(matches[0].user_id) : null;
    } catch {
      return null;
    }
  }

  private static readonly UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /**
   * DM rooms are keyed on Keycloak ids. Tap sites sometimes carry a profile document id instead
   * (e.g. a trip's `source_profile._id`), which makes the backend's Synapse invite fail with a 503.
   * A profile's `audit_data.create_user_id` is its owner's Keycloak id.
   */
  private async toKeycloakId(id: string): Promise<string | null> {
    if (VesselCardChatService.UUID.test(id)) return id;
    try {
      const profile: any = await firstValueFrom(this.profiles.getProfileById(id));
      const kc = String(profile?.audit_data?.create_user_id ?? '').trim();
      return VesselCardChatService.UUID.test(kc) ? kc : null;
    } catch {
      return null;
    }
  }

  /**
   * Messages is the `/chat` route, which opens whatever room id is pending once the room list has
   * it (and keeps retrying while Matrix is still syncing). Navigate first and join best-effort: a
   * join/sync hiccup must not strand the user on the map after a successful room lookup.
   */
  private async openInMessages(roomId: string): Promise<void> {
    this.chat.setPendingRoomOpen(roomId);
    this.submenu.setMessagesOpenState(true);
    await this.router.navigate(['/chat']);
    try {
      await this.chat.ensureRoomJoined(roomId);
      this.chat.openRoom(roomId);
    } catch (err) {
      console.warn('[VesselCardChat] join failed; Messages will retry while syncing', err);
    }
  }
}
